<template>
  <div class="enquiry-dashboard pa-2">
    <v-row>
      <!-- Top Enquiry KPI Cards -->
      <v-col cols="12">
        <v-card outlined elevation="0" class="pa-4 rounded-lg white">
          <div class="d-flex align-center mb-3">
            <v-icon color="cyan darken-3" class="mr-2"
              >mdi-help-circle-outline</v-icon
            >
            <span
              class="font-weight-700 color-custom-blue font-size-18 text-uppercase"
            >
              Enquiry Summary
            </span>
          </div>
          <v-row class="flex-nowrap-md">
            <v-col
              v-for="(item, index) in dEnquiryArr"
              :key="index"
              cols="12"
              sm="6"
              md=""
              class="flex-grow-1"
            >
              <v-card
                outlined
                elevation="0"
                :color="`${item.color} lighten-5`"
                :style="`border-color: ${getBgColor(item.color)} !important`"
                class="pa-3 h-100 rounded-lg"
              >
                <div class="d-flex align-center justify-space-between mb-2">
                  <span class="font-weight-600 font-size-14 color-custom-blue">
                    {{ item.title }}
                  </span>
                  <v-icon :size="24" :color="item.iconColor">
                    {{ item.icon }}
                  </v-icon>
                </div>
                <div class="d-flex align-center justify-space-between mt-1">
                  <span
                    :class="`font-size-26 font-weight-700 ${item.textColor}`"
                  >
                    {{ item.value }}
                  </span>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-card>
      </v-col>

      <!-- Duration Filter Control -->
      <v-col cols="12" class="d-flex justify-end py-1">
        <div style="max-width: 240px">
          <v-select
            :menu-props="{ offsetY: true }"
            dense
            filled
            solo
            flat
            outlined
            :items="durationList2"
            v-model="duration"
            hide-details
            :disabled="issueCountLoadingNew"
            item-color="cyan"
            class="custom-menu-select font-weight-600"
            @change="handleDurationChange"
          ></v-select>
        </div>
      </v-col>

      <!-- Category List Box -->
      <v-col cols="12" md="4">
        <v-card outlined elevation="0" class="pa-4 rounded-lg white h-100">
          <div class="d-flex align-center mb-2">
            <v-icon color="cyan darken-3" class="mr-2"
              >mdi-shape-outline</v-icon
            >
            <span
              class="font-weight-700 color-custom-blue font-size-16 text-uppercase"
            >
              Enquiry Category
            </span>
          </div>
          <div class="font-size-13 text-muted mb-4">
            Enquiry category distribution for team decision-making and impact
            reporting.
          </div>

          <div v-if="enquiryCategory && enquiryCategory.length > 0">
            <v-row dense>
              <v-col
                cols="12"
                v-for="(enquiry, index) in enquiryCategory"
                :key="index"
                class="cursor-pointer mb-1"
                @click="categoryBarClick(index, enquiry.category)"
              >
                <div
                  class="d-flex align-center justify-space-between pa-2 rounded border"
                  :class="
                    activeIndex === index
                      ? 'cyan lighten-5 border-cyan'
                      : 'grey lighten-5'
                  "
                >
                  <div class="d-flex align-center">
                    <v-icon
                      small
                      class="mr-2"
                      :color="
                        activeIndex === index
                          ? 'cyan darken-2'
                          : 'grey darken-1'
                      "
                    >
                      {{
                        activeIndex === index
                          ? "mdi-check-circle"
                          : "mdi-chevron-right"
                      }}
                    </v-icon>
                    <span
                      class="font-size-13 font-weight-600"
                      :class="
                        activeIndex === index
                          ? 'cyan--text text--darken-3'
                          : 'color-custom-blue'
                      "
                    >
                      {{ enquiry.category }}
                    </span>
                  </div>
                  <div>
                    <v-progress-circular
                      v-if="countLoading"
                      indeterminate
                      size="20"
                      width="2"
                      color="cyan"
                    ></v-progress-circular>
                    <v-chip
                      v-else
                      x-small
                      dark
                      :color="getUniqueColor(index)"
                      class="font-weight-700 px-2"
                    >
                      {{ enquiry.count }}
                    </v-chip>
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>
          <div v-else class="text-center py-6 text-muted font-size-14">
            No enquiry categories found
          </div>
        </v-card>
      </v-col>

      <!-- Enquiry by Priority -->
      <v-col cols="12" md="4">
        <v-card outlined elevation="0" class="pa-4 rounded-lg white h-100">
          <div class="d-flex align-center mb-2">
            <v-icon color="cyan darken-3" class="mr-2"
              >mdi-alert-circle-outline</v-icon
            >
            <span
              class="font-weight-700 color-custom-blue font-size-16 text-uppercase"
            >
              Enquiry by Priority
            </span>
          </div>
          <div class="font-size-13 text-muted mb-4">
            Unresolved support tickets grouped by priority level.
          </div>
          <div id="chart" class="d-flex justify-center">
            <apexchart
              type="donut"
              width="360"
              :options="ticketsByPrioritychartOptions"
              :series="enquiryByPriority"
              @dataPointSelection="onBarClickEnquiryPriority"
            ></apexchart>
          </div>
        </v-card>
      </v-col>

      <!-- Enquiry by Impact -->
      <v-col cols="12" md="4">
        <v-card outlined elevation="0" class="pa-4 rounded-lg white h-100">
          <div class="d-flex align-center mb-2">
            <v-icon color="cyan darken-3" class="mr-2"
              >mdi-lightning-bolt-outline</v-icon
            >
            <span
              class="font-weight-700 color-custom-blue font-size-16 text-uppercase"
            >
              Enquiry by Impact
            </span>
          </div>
          <div class="font-size-13 text-muted mb-4">
            Unresolved support tickets grouped by impact level.
          </div>
          <div id="chart" class="d-flex justify-center">
            <apexchart
              type="donut"
              width="360"
              :options="impactchartOptions"
              :series="enquiryByImpact"
              @dataPointSelection="onBarClickEnquiryImpect"
            ></apexchart>
          </div>
        </v-card>
      </v-col>

      <!-- Category-Wise Bar Chart -->
      <v-col cols="12">
        <v-card outlined elevation="0" class="pa-4 rounded-lg white">
          <div class="d-flex align-center justify-space-between mb-4">
            <div class="d-flex align-center">
              <v-icon color="cyan darken-3" class="mr-2"
                >mdi-chart-bar-stacked</v-icon
              >
              <span
                class="font-weight-700 color-custom-blue font-size-18 text-uppercase"
              >
                Enquiry Category-Wise
              </span>
              <v-chip x-small color="green" dark class="ml-2 font-weight-600"
                >Top 5</v-chip
              >
            </div>
          </div>
          <div id="chart" class="enquiry-category">
            <apexchart
              type="bar"
              height="350"
              :options="categoryTicketschartOptions"
              :series="categoryTicketsseries"
              @dataPointSelection="onBarClickEnquiryCategory"
            ></apexchart>
          </div>
        </v-card>
      </v-col>

      <!-- New & Closed Line Chart -->
      <v-col cols="12" md="8">
        <v-card outlined elevation="0" class="pa-4 rounded-lg white h-100">
          <div class="d-flex align-center mb-4">
            <v-icon color="cyan darken-3" class="mr-2"
              >mdi-chart-timeline-variant</v-icon
            >
            <span
              class="font-weight-700 color-custom-blue font-size-18 text-uppercase"
            >
              New & Closed Enquiries (6 Months)
            </span>
          </div>
          <div id="chart">
            <apexchart
              type="line"
              height="350"
              :options="newClosedEnquirychartOptions"
              :series="newClosedEnquiryseries"
            ></apexchart>
          </div>
        </v-card>
      </v-col>

      <!-- Response Time Box -->
      <v-col cols="12" md="4">
        <v-card outlined elevation="0" class="pa-4 rounded-lg white h-100">
          <div class="d-flex align-center mb-2">
            <v-icon color="cyan darken-3" class="mr-2">mdi-timer-sand</v-icon>
            <span
              class="font-weight-700 color-custom-blue font-size-18 text-uppercase"
            >
              Response Time
            </span>
          </div>
          <div class="font-size-13 text-muted mb-4">
            Average resolution time across all enquiry tickets.
          </div>
          <div class="d-flex justify-center align-center h-75">
            <v-card
              outlined
              elevation="0"
              color="cyan lighten-5"
              class="pa-4 rounded-lg text-center w-100 border-cyan"
            >
              <v-icon color="cyan darken-3" size="48" class="mb-2"
                >mdi-clock-check-outline</v-icon
              >
              <div
                class="font-size-20 font-weight-700 cyan--text text--darken-4"
              >
                {{ resolveDay }} Days, {{ resolveHours }} Hrs,<br />
                {{ resolveMins }} Mins
              </div>
            </v-card>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import { GET } from "@/core/services/store/request.module";
export default {
  name: "ProjectStatus",
  data() {
    return {
      countLoading: false,
      issueCountLoadingNew: false,
      activeIndex: null,
      enquiryCategory: [],
      enquiryByPriority: [0, 0, 0],
      enquiryByImpact: [0, 0, 0],
      categoryWithStatus: [],
      categoriesList: [],
      monthsList: [],
      resolveDay: 10,
      resolveHours: 4,
      resolveMins: 50,
      enquirySummaryData: [
        {
          title: "Open",
          value: 42,
          color: "indigo",
          textColor: "indigo--text",
          iconColor: "indigo",
          icon: "mdi-account-question-outline",
        },
        {
          title: "In Progress",
          value: 12,
          color: "yellow",
          textColor: "yellow--text",
          iconColor: "yellow",
          icon: "mdi-account-question-outline",
        },
        {
          title: "On Hold",
          value: 1,
          color: "orange",
          textColor: "orange--text",
          iconColor: "orange",
          icon: "mdi-account-question-outline",
        },
        {
          title: "Completed",
          value: 4,
          color: "green",
          textColor: "green--text",
          iconColor: "green",
          icon: "mdi-account-question-outline",
        },
        {
          title: "Cancelled",
          value: 1,
          color: "red",
          textColor: "red--text",
          iconColor: "red",
          icon: "mdi-account-question-outline",
        },
      ],
      newClosedEnquiryseries: [],

      filteredticketsByPriorityseries: [],
      ticketsByPriorityseries: [43, 12, 15],
      ticketsByPrioritychartOptions: {
        chart: {
          width: 425,
          type: "donut",
        },
        plotOptions: {
          pie: {
            startAngle: -90,
            endAngle: 270,
          },
        },
        labels: ["High", "Medium", "Low"],
        colors: ["#c62828", "#e65100", "#558b2f"],
        dataLabels: {
          enabled: true,
        },

        fill: {
          type: "gradient",
        },
        legend: {
          formatter: function (val, opts) {
            return val + " - " + opts.w.globals.series[opts.seriesIndex];
          },
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 200,
              },
              legend: {
                show: false,
                position: "bottom",
              },
            },
          },
        ],
      },
      filterimpactseries: [],
      impactchartOptions: {
        chart: {
          width: 425,
          type: "donut",
        },
        plotOptions: {
          pie: {
            startAngle: -90,
            endAngle: 270,
          },
        },
        colors: ["#c62828", "#e65100", "#558b2f"],
        labels: ["Highest", "Medium", "Lowest"],
        dataLabels: {
          enabled: true,
        },

        fill: {
          type: "gradient",
        },
        legend: {
          formatter: function (val, opts) {
            return val + " - " + opts.w.globals.series[opts.seriesIndex];
          },
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 200,
              },
              legend: {
                show: false,
                position: "bottom",
              },
            },
          },
        ],
      },

      ticketsByStatusseries: [42, 12, 1, 4, 1],
      ticketsByStatuschartOptions: {
        chart: {
          width: 425,
          type: "donut",
        },
        plotOptions: {
          pie: {
            startAngle: -90,
            endAngle: 270,
          },
        },
        labels: [
          "New",
          "Open",
          "In-Progress",
          "Overdue",
          "Closed",
          "Escalated",
        ],
        dataLabels: {
          enabled: false,
        },
        fill: {
          type: "gradient",
        },
        legend: {
          formatter: function (val, opts) {
            return val + " - " + opts.w.globals.series[opts.seriesIndex];
          },
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 200,
              },
              legend: {
                show: false,
                position: "bottom",
              },
            },
          },
        ],
      },
      categoryTicketsseries: [
        {
          name: "Open",
          data: [],
        },
        {
          name: "In Progress",
          data: [],
        },
        {
          name: "On Hold",
          data: [],
        },
        {
          name: "Cancelled",
          data: [],
        },

        {
          name: "Closed",
          data: [],
        },
        {
          name: "Escalated",
          data: [],
        },
      ],

      categoryList: [
        { text: "Open", value: "open" },
        { text: "Closed", value: "closed" },
        { text: "Overdue", value: "overdue" },
      ],
      isCategory: "open",
      durationList: [
        { text: "This Week", value: "this_week" },
        { text: "This Month", value: "this_month" },
        { text: "Last 3 Month", value: "last-3month" },
        { text: "Last 6 Month", value: "last-6month" },
      ],
      durationList2: [
        { text: "This Week", value: "this_week" },
        { text: "Last Week", value: "last_week" },
        { text: "This Month", value: "this_month" },
        { text: "Last Month", value: "last_month" },
        { text: "Last 3 Month", value: "last-3month" },
        { text: "Last 6 Month", value: "last-6month" },
      ],

      duration: "this_week",
      network: 63,
      software: 46,
      other: 43,
      hardware: 33,
    };
  },

  created() {
    this.filteredticketsByPriorityseries = this.ticketsByPriorityseries;
    this.filterimpactseries = this.impactseries;
  },

  mounted() {
    this.getEnquiryChartData();
  },
  computed: {
    ...mapGetters(["dEnquiryArr"]),

    newClosedEnquirychartOptions() {
      return {
        chart: {
          height: 350,
          type: "line",
          dropShadow: {
            enabled: true,
            color: "#000",
            top: 18,
            left: 7,
            blur: 10,
            opacity: 0.2,
          },
          zoom: {
            enabled: false,
          },
          toolbar: {
            show: false,
          },
        },
        colors: ["#4CAF50", "#F44336"],
        dataLabels: {
          enabled: true,
        },
        stroke: {
          curve: "smooth",
        },
        title: {
          text: "Average New & Closed Enquiries",
          align: "left",
        },
        grid: {
          borderColor: "#e7e7e7",
          row: {
            colors: ["#f3f3f3", "transparent"],
            opacity: 0.5,
          },
        },
        markers: {
          size: 1,
        },
        xaxis: {
          categories: this.monthsList,
          title: {
            text: "Month",
          },
        },
        yaxis: {
          title: {
            text: "Enquiries",
          },
        },
        legend: {
          position: "top",
          horizontalAlign: "right",
          floating: true,
          offsetY: -25,
          offsetX: -5,
        },
      };
    },

    categoryTicketschartOptions() {
      return {
        chart: {
          type: "bar",
          height: 350,
          stacked: true,
          toolbar: {
            show: false,
          },
        },
        plotOptions: {
          bar: {
            horizontal: true,
            dataLabels: {
              total: {
                enabled: true,
                offsetX: 0,
                style: {
                  fontSize: "13px",
                  fontWeight: 900,
                },
              },
            },
          },
        },
        colors: [
          "#26a0fc",
          "#cccc0066",
          "#ffa50060",
          "#B71C1C",
          "#B71C1C",
          "#E53935",
        ],
        stroke: {
          width: 1,
          colors: ["#fff"],
        },
        title: {
          text: "New and open tickets categorized by type, showing the count and status for each category. New and open tickets categorized by type, showing the count and status for each category.",
          align: "left",
        },

        dataLabels: {
          enabled: true,
          style: {
            colors: ["#000"],
          },
        },
        xaxis: {
          categories: this.categoriesList,
          labels: {
            formatter: function (val) {
              return val + "";
            },
          },
        },
        yaxis: {
          title: {
            text: undefined,
          },
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val + " Enquiries";
            },
          },
        },
        fill: {
          opacity: 1,
        },
        legend: {
          position: "top",
          horizontalAlign: "left",
          offsetX: 40,
        },
      };
    },
  },
  methods: {
    categoryBarClick(index, category) {
      this.activeIndex = index;
      console.log(category);
      this.ticketsByPrioritychartOptions = {
        ...this.ticketsByPrioritychartOptions,
        colors: ["#c62828", "#e65100", "#558b2f"],
      };
      this.impactchartOptions = {
        ...this.impactchartOptions,
        colors: ["#c62828", "#e65100", "#558b2f"],
      };
      const params = {
        category: category,
        duration: this.duration,
      };
      this.fetchCategoryFilteredData(params);
    },

    getUniqueColor(index) {
      const hue = (index * 137) % 360;
      return `hsl(${hue}, 70%, 60%)`;
    },

    onBarClick(index) {
      this.activeIndex = index;
    },

    async fetchCategoryFilteredData(params) {
      try {
        const queryString = new URLSearchParams(params).toString();

        const requestEndpoint = `enquiry-chart-data?${queryString}`;

        console.log(requestEndpoint);
        const response = await this.$store.dispatch(GET, {
          url: requestEndpoint,
        });

        if (response.data) {
          if (response.data.enquiryByPriority) {
            this.enquiryByPriority = response.data.enquiryByPriority.map(
              (item) => item.count
            );
          }

          if (response.data.enquiryByImpact) {
            this.enquiryByImpact = response.data.enquiryByImpact.map(
              (item) => item.count
            );
          }

          if (response.data.categoryWithStatus) {
            this.categoryWithStatus = response.data.categoryWithStatus;
            this.categoriesList = Object.keys(response.data.categoryWithStatus);
            this.categoryTicketsseries.forEach((series) => {
              series.data = [];
            });
            this.categoriesList.forEach((category) => {
              const categoryData = response.data.categoryWithStatus[category];
              this.categoryTicketsseries.forEach((series) => {
                const statusData = categoryData.find(
                  (item) => item.status_title === series.name
                );
                series.data.push(statusData ? statusData.count : 0);
              });
            });
          }

          if (response.data.newClosedEnquiries) {
            this.monthsList = response.data.newClosedEnquiries.months;
            this.newClosedEnquiryseries = [];
            if (response.data.newClosedEnquiries) {
              this.newClosedEnquiryseries.push({
                name: "New Enquiries",
                data: response.data.newClosedEnquiries["0"]?.data || [],
              });

              this.newClosedEnquiryseries.push({
                name: "Closed Enquiries",
                data: response.data.newClosedEnquiries["1"]?.data || [],
              });
            }
          }
        }
      } catch (error) {
        console.error("Error fetching filtered data:", error);
      }
    },

    handleDurationChange() {
      const params = {
        duration: this.duration,
      };
      this.getFilteredEnquiry(params);
    },

    async getFilteredEnquiry(params) {
      try {
        const queryString = new URLSearchParams(params).toString();

        const requestEndpoint = `enquiry-chart-data?${queryString}`;

        const response = await this.$store.dispatch(GET, {
          url: requestEndpoint,
        });

        if (response.data) {
          this.enquiryCategory = response.data.enquiryByCategory;
          if (response.data.enquiryByPriority) {
            this.enquiryByPriority = response.data.enquiryByPriority.map(
              (item) => item.count
            );
          }

          if (response.data.enquiryByImpact) {
            this.enquiryByImpact = response.data.enquiryByImpact.map(
              (item) => item.count
            );
          }

          if (response.data.categoryWithStatus) {
            this.impactchartOptions = {
              ...this.impactchartOptions,
              colors: ["#c62828", "#e65100", "#558b2f"],
            };

            this.ticketsByPrioritychartOptions = {
              ...this.ticketsByPrioritychartOptions,
              colors: ["#c62828", "#e65100", "#558b2f"],
            };

            this.categoryWithStatus = response.data.categoryWithStatus;
            this.categoriesList = Object.keys(response.data.categoryWithStatus);
            this.categoryTicketsseries.forEach((series) => {
              series.data = [];
            });
            this.categoriesList.forEach((category) => {
              const categoryData = response.data.categoryWithStatus[category];
              this.categoryTicketsseries.forEach((series) => {
                const statusData = categoryData.find(
                  (item) => item.status_title === series.name
                );
                series.data.push(statusData ? statusData.count : 0);
              });
            });
          }

          if (response.data.newClosedEnquiries) {
            this.monthsList = response.data.newClosedEnquiries.months;
            this.newClosedEnquiryseries = [];
            if (response.data.newClosedEnquiries) {
              this.newClosedEnquiryseries.push({
                name: "New Enquiries",
                data: response.data.newClosedEnquiries["0"]?.data || [],
              });

              this.newClosedEnquiryseries.push({
                name: "Closed Enquiries",
                data: response.data.newClosedEnquiries["1"]?.data || [],
              });
            }
          }
        }
      } catch (error) {
        console.error("Error fetching filtered data:", error);
      }
    },

    onBarClickImpactPriority(event, chartContext, config) {
      this.activeIndex = null;
      this.impactchartOptions = {
        ...this.impactchartOptions,
        colors: ["#c62828", "#e65100", "#558b2f"],
      };

      const clickedIndex = config.dataPointIndex;

      let priority = null;
      switch (clickedIndex) {
        case 0:
          priority = 1;
          break;
        case 1:
          priority = 2;
          break;
        case 2:
          priority = 3;
          break;
        default:
          priority = 1;
      }

      const params = {
        priority: priority,
      };

      this.disableOtherPriorityBars(clickedIndex);
      this.fetchPriorityFilteredData(params);
    },

    onBarClickEnquiryPriority(event, chartContext, config) {
      this.activeIndex = null;
      this.impactchartOptions = {
        ...this.impactchartOptions,
        colors: ["#c62828", "#e65100", "#558b2f"],
      };

      const clickedIndex = config.dataPointIndex;

      let priority = null;
      switch (clickedIndex) {
        case 0:
          priority = 1;
          break;
        case 1:
          priority = 2;
          break;
        case 2:
          priority = 3;
          break;
        default:
          priority = 1;
      }

      const params = {
        priority: priority,
        duration: this.duration,
      };

      this.disableOtherPriorityBars(clickedIndex);
      this.fetchPriorityFilteredData(params);
    },

    async fetchPriorityFilteredData(params) {
      try {
        const queryString = new URLSearchParams(params).toString();

        const requestEndpoint = `enquiry-chart-data?${queryString}`;

        const response = await this.$store.dispatch(GET, {
          url: requestEndpoint,
        });

        if (response.data) {
          this.enquiryCategory = response.data.enquiryByCategory;
          if (response.data.enquiryByImpact) {
            this.enquiryByImpact = response.data.enquiryByImpact.map(
              (item) => item.count
            );
          }
          if (response.data.categoryWithStatus) {
            this.categoryWithStatus = response.data.categoryWithStatus;
            this.categoriesList = Object.keys(response.data.categoryWithStatus);
            this.categoryTicketsseries.forEach((series) => {
              series.data = [];
            });
            this.categoriesList.forEach((category) => {
              const categoryData = response.data.categoryWithStatus[category];
              this.categoryTicketsseries.forEach((series) => {
                const statusData = categoryData.find(
                  (item) => item.status_title === series.name
                );
                series.data.push(statusData ? statusData.count : 0);
              });
            });
          }
          if (response.data.newClosedEnquiries) {
            this.monthsList = response.data.newClosedEnquiries.months;

            this.newClosedEnquiryseries = [];

            if (response.data.newClosedEnquiries) {
              this.newClosedEnquiryseries.push({
                name: "New Enquiries",
                data: response.data.newClosedEnquiries["0"]?.data || [],
              });

              this.newClosedEnquiryseries.push({
                name: "Closed Enquiries",
                data: response.data.newClosedEnquiries["1"]?.data || [],
              });
            }
          }
        }
      } catch (error) {
        console.error("Error fetching filtered data:", error);
      }
    },

    disableOtherPriorityBars(selectedIndex) {
      const defaultColors = ["#c62828", "#e65100", "#558b2f"];
      const preColor = defaultColors.map((color, index) =>
        index === selectedIndex ? color : "#d3d3d3"
      );
      this.ticketsByPrioritychartOptions = {
        ...this.ticketsByPrioritychartOptions,
        colors: preColor,
      };
    },

    onBarClickEnquiryImpect(event, chartContext, config) {
      this.activeIndex = null;
      this.ticketsByPrioritychartOptions = {
        ...this.ticketsByPrioritychartOptions,
        colors: ["#c62828", "#e65100", "#558b2f"],
      };

      const clickedIndex = config.dataPointIndex;

      let impact = null;
      switch (clickedIndex) {
        case 0:
          impact = 1;
          break;
        case 1:
          impact = 2;
          break;
        case 2:
          impact = 3;
          break;
        default:
          impact = 1;
      }

      const params = {
        impact: impact,
        duration: this.duration,
      };

      this.disableOtherImpectBars(clickedIndex);
      this.fetchImpactFilteredData(params);
    },

    async fetchImpactFilteredData(params) {
      try {
        const queryString = new URLSearchParams(params).toString();

        const requestEndpoint = `enquiry-chart-data?${queryString}`;

        const response = await this.$store.dispatch(GET, {
          url: requestEndpoint,
        });

        if (response.data) {
          this.enquiryCategory = response.data.enquiryByCategory;

          if (response.data.enquiryByPriority) {
            this.enquiryByPriority = response.data.enquiryByPriority.map(
              (item) => item.count
            );
          }

          if (response.data.categoryWithStatus) {
            this.categoryWithStatus = response.data.categoryWithStatus;
            this.categoriesList = Object.keys(response.data.categoryWithStatus);
            this.categoryTicketsseries.forEach((series) => {
              series.data = [];
            });
            this.categoriesList.forEach((category) => {
              const categoryData = response.data.categoryWithStatus[category];
              this.categoryTicketsseries.forEach((series) => {
                const statusData = categoryData.find(
                  (item) => item.status_title === series.name
                );
                series.data.push(statusData ? statusData.count : 0);
              });
            });
          }

          if (response.data.newClosedEnquiries) {
            this.monthsList = response.data.newClosedEnquiries.months;
            this.newClosedEnquiryseries = [];

            if (response.data.newClosedEnquiries) {
              this.newClosedEnquiryseries.push({
                name: "New Enquiries",
                data: response.data.newClosedEnquiries["0"]?.data || [],
              });

              this.newClosedEnquiryseries.push({
                name: "Closed Enquiries",
                data: response.data.newClosedEnquiries["1"]?.data || [],
              });
            }
          }
        }
      } catch (error) {
        console.error("Error fetching filtered data:", error);
      }
    },

    disableOtherImpectBars(selectedIndex) {
      const defaultColors = ["#c62828", "#e65100", "#558b2f"];
      const preColor = defaultColors.map((color, index) =>
        index === selectedIndex ? color : "#d3d3d3"
      );
      this.impactchartOptions = {
        ...this.impactchartOptions,
        colors: preColor,
      };
    },

    onBarClickEnquiryCategory(event, chartContext, config) {
      this.activeIndex = null;
      this.ticketsByPrioritychartOptions = {
        ...this.ticketsByPrioritychartOptions,
        colors: ["#c62828", "#e65100", "#558b2f"],
      };
      this.impactchartOptions = {
        ...this.impactchartOptions,
        colors: ["#c62828", "#e65100", "#558b2f"],
      };
      const clickedIndex = config.dataPointIndex;
      const category = this.categoriesList[clickedIndex];
      const seriesIndex = config.seriesIndex;
      const statusIndex = seriesIndex + 1;

      const params = {
        category: category,
        status: statusIndex,
        duration: this.duration,
      };
      this.fetchCategoryWiseEnquiry(params);
      this.disableEnquiryCategoryBars(clickedIndex);
    },

    disableEnquiryCategoryBars(clickedIndex) {
      this.$nextTick(() => {
        const bars = document.querySelectorAll(
          ".enquiry-category .apexcharts-yaxis-label"
        );
        bars.forEach((bar, index) => {
          if (index === clickedIndex) {
            bar.classList.add("active-bar");
          } else {
            bar.classList.remove("active-bar");
            // bar.classList.add("inactive-bar");
          }
        });
      });
    },

    async fetchCategoryWiseEnquiry(params) {
      try {
        const queryString = new URLSearchParams(params).toString();
        console.log(queryString);

        const requestEndpoint = `enquiry-chart-data?${queryString}`;

        const response = await this.$store.dispatch(GET, {
          url: requestEndpoint,
        });

        if (response.data) {
          this.enquiryCategory = response.data.enquiryByCategory;
          if (response.data.enquiryByPriority) {
            this.enquiryByPriority = response.data.enquiryByPriority.map(
              (item) => item.count
            );
          }
          if (response.data.enquiryByImpact) {
            this.enquiryByImpact = response.data.enquiryByImpact.map(
              (item) => item.count
            );
          }

          if (response.data.newClosedEnquiries) {
            this.monthsList = response.data.newClosedEnquiries.months;
            this.newClosedEnquiryseries = [];
            if (response.data.newClosedEnquiries) {
              this.newClosedEnquiryseries.push({
                name: "New Enquiries",
                data: response.data.newClosedEnquiries["0"]?.data || [],
              });

              this.newClosedEnquiryseries.push({
                name: "Closed Enquiries",
                data: response.data.newClosedEnquiries["1"]?.data || [],
              });
            }
          }
        }
      } catch (error) {
        console.error("Error fetching filtered data:", error);
      }
    },

    async fetchFilteredNewClosedEnquiry(params) {
      try {
        const queryString = new URLSearchParams(params).toString();
        const requestEndpoint = `enquiry-chart-data?${queryString}`;
        const response = await this.$store.dispatch(GET, {
          url: requestEndpoint,
        });

        if (response.data) {
          this.enquiryCategory = response.data.enquiryByCategory;

          if (response.data.enquiryByPriority) {
            this.enquiryByPriority = response.data.enquiryByPriority.map(
              (item) => item.count
            );
          }

          if (response.data.enquiryByImpact) {
            this.enquiryByImpact = response.data.enquiryByImpact.map(
              (item) => item.count
            );
          }

          if (response.data.categoryWithStatus) {
            this.categoryWithStatus = response.data.categoryWithStatus;
            this.categoriesList = Object.keys(response.data.categoryWithStatus);
            this.categoryTicketsseries.forEach((series) => {
              series.data = [];
            });
            this.categoriesList.forEach((category) => {
              const categoryData = response.data.categoryWithStatus[category];
              this.categoryTicketsseries.forEach((series) => {
                const statusData = categoryData.find(
                  (item) => item.status_title === series.name
                );
                series.data.push(statusData ? statusData.count : 0);
              });
            });
          }

          if (response.data.newClosedEnquiries) {
            this.monthsList = response.data.newClosedEnquiries.months;
            this.newClosedEnquiryseries = [];
            if (response.data.newClosedEnquiries) {
              this.newClosedEnquiryseries.push({
                name: "New Enquiries",
                data: response.data.newClosedEnquiries["0"]?.data || [],
              });

              this.newClosedEnquiryseries.push({
                name: "Closed Enquiries",
                data: response.data.newClosedEnquiries["1"]?.data || [],
              });
            }
          }
        }
      } catch (error) {
        console.error("Error fetching filtered data:", error);
      }
    },

    async fetchFilteredCategoryWiseEnquiry(params) {
      try {
        const queryString = new URLSearchParams(params).toString();
        const requestEndpoint = `enquiry-chart-data?${queryString}`;
        const response = await this.$store.dispatch(GET, {
          url: requestEndpoint,
        });

        if (response.data) {
          this.enquiryCategory = response.data.enquiryByCategory;

          if (response.data.enquiryByPriority) {
            this.enquiryByPriority = response.data.enquiryByPriority.map(
              (item) => item.count
            );
          }

          if (response.data.enquiryByImpact) {
            this.enquiryByImpact = response.data.enquiryByImpact.map(
              (item) => item.count
            );
          }

          if (response.data.categoryWithStatus) {
            this.categoryWithStatus = response.data.categoryWithStatus;
            this.categoriesList = Object.keys(response.data.categoryWithStatus);
            this.categoryTicketsseries.forEach((series) => {
              series.data = [];
            });
            this.categoriesList.forEach((category) => {
              const categoryData = response.data.categoryWithStatus[category];
              this.categoryTicketsseries.forEach((series) => {
                const statusData = categoryData.find(
                  (item) => item.status_title === series.name
                );
                series.data.push(statusData ? statusData.count : 0);
              });
            });
          }

          if (response.data.newClosedEnquiries) {
            this.monthsList = response.data.newClosedEnquiries.months;
            this.newClosedEnquiryseries = [];
            if (response.data.newClosedEnquiries) {
              this.newClosedEnquiryseries.push({
                name: "New Enquiries",
                data: response.data.newClosedEnquiries["0"]?.data || [],
              });

              this.newClosedEnquiryseries.push({
                name: "Closed Enquiries",
                data: response.data.newClosedEnquiries["1"]?.data || [],
              });
            }
          }
        }
      } catch (error) {
        console.error("Error fetching filtered data:", error);
      }
    },
    getBgColor(color) {
      switch (color) {
        case "cyan":
          return "#00bcd4";
        case "green":
          return "#4CAF50";
        case "red":
          return "#F44336";
        case "blue":
          return "#ff9800";
        case "orange":
          return "#ff9800";
        case "yellow":
          return "#ffeb3b";
        case "indigo":
          return "#3f51b5";
        case "purple":
          return "#9c27b0";
        case "red lighten-3":
          return "#F44336";
        default:
          return "white";
      }
    },

    async getEnquiryChartData() {
      try {
        const response = await this.$store.dispatch(GET, {
          url: "enquiry-chart-data",
        });

        if (response.data) {
          this.enquiryCategory = response.data.enquiryByCategory;

          if (response.data.enquiryByPriority) {
            this.enquiryByPriority = response.data.enquiryByPriority.map(
              (item) => item.count
            );
          }

          if (response.data.enquiryByImpact) {
            this.enquiryByImpact = response.data.enquiryByImpact.map(
              (item) => item.count
            );
          }

          if (response.data.categoryWithStatus) {
            this.categoryWithStatus = response.data.categoryWithStatus;
            this.categoriesList = Object.keys(response.data.categoryWithStatus);
            this.categoryTicketsseries.forEach((series) => {
              series.data = [];
            });
            this.categoriesList.forEach((category) => {
              const categoryData = response.data.categoryWithStatus[category];
              this.categoryTicketsseries.forEach((series) => {
                const statusData = categoryData.find(
                  (item) => item.status_title === series.name
                );
                series.data.push(statusData ? statusData.count : 0);
              });
            });
          }

          if (response.data.newClosedEnquiries) {
            this.monthsList = response.data.newClosedEnquiries.months;

            if (response.data.newClosedEnquiries) {
              this.newClosedEnquiryseries.push({
                name: "New Enquiries",
                data: response.data.newClosedEnquiries["0"]?.data || [],
              });

              this.newClosedEnquiryseries.push({
                name: "Closed Enquiries",
                data: response.data.newClosedEnquiries["1"]?.data || [],
              });
            }
          }
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      }
    },
  },
};
</script>
<style scoped src="@/assets/sass/dashboard.scss" lang="scss">
@media screen and (min-width: 1200px) and (max-width: 1399px) {
  .v-application .text-h5 {
    font-size: 1.2rem !important;
  }
}
</style>
